// src/components/system/i18n/i18nManager.js
// Internationalization Management System
// Created: 2025-01-20 16:51:00 UTC+8
// Purpose: Enhanced i18n management with website configuration integration

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';

/**
 * Enhanced i18n Manager Class
 * Extends the existing i18n system with configuration-based management
 */
export class I18nManager {
  constructor(config) {
    this.config = config;
    this.i18nInstance = null;
    this.websiteTranslations = {};
  }

  /**
   * Initialize i18n with configuration
   * @param {Object} additionalOptions - Additional i18n options
   * @returns {Promise} - i18n initialization promise
   */
  async initialize(additionalOptions = {}) {
    const { i18n: i18nConfig } = this.config;
    
    // Load website-specific translations
    await this.loadWebsiteTranslations();

    const options = {
      fallbackLng: i18nConfig.fallbackLanguage,
      lng: i18nConfig.defaultLanguage,
      debug: process.env.NODE_ENV === 'development',
      
      interpolation: {
        escapeValue: false,
      },
      
      detection: {
        order: ['querystring', 'cookie', 'localStorage', 'navigator', 'htmlTag'],
        lookupQuerystring: 'lang',
        lookupCookie: 'i18next',
        lookupLocalStorage: 'i18nextLng',
        caches: ['localStorage', 'cookie'],
      },
      
      resources: this.buildResources(),
      
      ...additionalOptions
    };

    if (!this.i18nInstance) {
      this.i18nInstance = i18n.createInstance();
      
      await this.i18nInstance
        .use(Backend)
        .use(LanguageDetector)
        .use(initReactI18next)
        .init(options);
    }

    return this.i18nInstance;
  }

  /**
   * Load website-specific translations based on configuration
   */
  async loadWebsiteTranslations() {
    const { website, contact, navigation } = this.config;
    
    // Generate website-specific translations for each supported language
    this.config.i18n.supportedLanguages.forEach(lang => {
      this.websiteTranslations[lang] = {
        // Website information
        'website.title': this.getLocalizedText(website.title, lang),
        'website.subtitle': this.getLocalizedText(website.subtitle, lang),
        'website.description': this.getLocalizedText(website.description, lang),
        
        // Contact information
        'contact.email': contact.email,
        'contact.phone': contact.phone,
        'contact.address': this.getLocalizedText(contact.address, lang),
        
        // Navigation
        ...this.generateNavigationTranslations(navigation.menu, lang),
        
        // Common UI elements
        'ui.loading': this.getLoadingText(lang),
        'ui.error': this.getErrorText(lang),
        'ui.success': this.getSuccessText(lang),
        'ui.cancel': this.getCancelText(lang),
        'ui.save': this.getSaveText(lang),
        'ui.edit': this.getEditText(lang),
        'ui.delete': this.getDeleteText(lang),
        'ui.confirm': this.getConfirmText(lang),
        
        // Date and time formats
        'format.date': this.config.i18n.dateFormat[lang],
        'format.time': this.getTimeFormat(lang),
        
        // SEO-related translations
        'seo.readMore': this.getReadMoreText(lang),
        'seo.shareOn': this.getShareOnText(lang),
        'seo.backToTop': this.getBackToTopText(lang)
      };
    });
  }

  /**
   * Build resources object for i18n initialization
   * @returns {Object} - Resources object
   */
  buildResources() {
    const resources = {};
    
    this.config.i18n.supportedLanguages.forEach(lang => {
      resources[lang] = {
        translation: {
          ...this.websiteTranslations[lang] || {},
          // Additional translations can be loaded from files
        }
      };
    });
    
    return resources;
  }

  /**
   * Generate navigation translations
   * @param {Array} menuItems - Navigation menu items
   * @param {string} lang - Language code
   * @returns {Object} - Navigation translations
   */
  generateNavigationTranslations(menuItems, lang) {
    const translations = {};
    
    menuItems.forEach(item => {
      translations[`nav.${item.key}`] = this.getLocalizedText(item.titleKey, lang);
    });
    
    return translations;
  }

  /**
   * Get localized text based on language
   * @param {string} key - Translation key
   * @param {string} lang - Language code
   * @returns {string} - Localized text
   */
  getLocalizedText(key, lang) {
    // This would typically load from translation files
    // For now, return the key as fallback
    const translations = {
      en: {
        'Home': 'Home',
        'Latest News': 'Latest News',
        'About Us': 'About Us',
        'Exhibitors': 'Exhibitors',
        'AI4Good': 'AI4Good'
      },
      sc: {
        'Home': '主页',
        'Latest News': '最新消息',
        'About Us': '关于我们',
        'Exhibitors': '参展商',
        'AI4Good': 'AI4Good'
      },
      tc: {
        'Home': '首頁',
        'Latest News': '最新消息',
        'About Us': '關於我們',
        'Exhibitors': '參展商',
        'AI4Good': 'AI4Good'
      },
      th: {
        'Home': 'หน้าแรก',
        'Latest News': 'ข่าวล่าสุด',
        'About Us': 'เกี่ยวกับเรา',
        'Exhibitors': 'ผู้จัดแสดง',
        'AI4Good': 'AI4Good'
      }
    };
    
    return translations[lang]?.[key] || key;
  }

  /**
   * Get loading text for language
   * @param {string} lang - Language code
   * @returns {string} - Loading text
   */
  getLoadingText(lang) {
    const texts = {
      en: 'Loading...',
      sc: '加载中...',
      tc: '載入中...',
      th: 'กำลังโหลด...'
    };
    return texts[lang] || texts.en;
  }

  /**
   * Get error text for language
   * @param {string} lang - Language code
   * @returns {string} - Error text
   */
  getErrorText(lang) {
    const texts = {
      en: 'Error',
      sc: '错误',
      tc: '錯誤',
      th: 'ข้อผิดพลาด'
    };
    return texts[lang] || texts.en;
  }

  /**
   * Get success text for language
   * @param {string} lang - Language code
   * @returns {string} - Success text
   */
  getSuccessText(lang) {
    const texts = {
      en: 'Success',
      sc: '成功',
      tc: '成功',
      th: 'สำเร็จ'
    };
    return texts[lang] || texts.en;
  }

  /**
   * Get cancel text for language
   * @param {string} lang - Language code
   * @returns {string} - Cancel text
   */
  getCancelText(lang) {
    const texts = {
      en: 'Cancel',
      sc: '取消',
      tc: '取消',
      th: 'ยกเลิก'
    };
    return texts[lang] || texts.en;
  }

  /**
   * Get save text for language
   * @param {string} lang - Language code
   * @returns {string} - Save text
   */
  getSaveText(lang) {
    const texts = {
      en: 'Save',
      sc: '保存',
      tc: '儲存',
      th: 'บันทึก'
    };
    return texts[lang] || texts.en;
  }

  /**
   * Get edit text for language
   * @param {string} lang - Language code
   * @returns {string} - Edit text
   */
  getEditText(lang) {
    const texts = {
      en: 'Edit',
      sc: '编辑',
      tc: '編輯',
      th: 'แก้ไข'
    };
    return texts[lang] || texts.en;
  }

  /**
   * Get delete text for language
   * @param {string} lang - Language code
   * @returns {string} - Delete text
   */
  getDeleteText(lang) {
    const texts = {
      en: 'Delete',
      sc: '删除',
      tc: '刪除',
      th: 'ลบ'
    };
    return texts[lang] || texts.en;
  }

  /**
   * Get confirm text for language
   * @param {string} lang - Language code
   * @returns {string} - Confirm text
   */
  getConfirmText(lang) {
    const texts = {
      en: 'Confirm',
      sc: '确认',
      tc: '確認',
      th: 'ยืนยัน'
    };
    return texts[lang] || texts.en;
  }

  /**
   * Get time format for language
   * @param {string} lang - Language code
   * @returns {string} - Time format
   */
  getTimeFormat(lang) {
    const formats = {
      en: 'HH:mm:ss',
      sc: 'HH:mm:ss',
      tc: 'HH:mm:ss',
      th: 'HH:mm:ss'
    };
    return formats[lang] || formats.en;
  }

  /**
   * Get read more text for language
   * @param {string} lang - Language code
   * @returns {string} - Read more text
   */
  getReadMoreText(lang) {
    const texts = {
      en: 'Read More',
      sc: '阅读更多',
      tc: '閱讀更多',
      th: 'อ่านเพิ่มเติม'
    };
    return texts[lang] || texts.en;
  }

  /**
   * Get share on text for language
   * @param {string} lang - Language code
   * @returns {string} - Share on text
   */
  getShareOnText(lang) {
    const texts = {
      en: 'Share on',
      sc: '分享到',
      tc: '分享到',
      th: 'แชร์บน'
    };
    return texts[lang] || texts.en;
  }

  /**
   * Get back to top text for language
   * @param {string} lang - Language code
   * @returns {string} - Back to top text
   */
  getBackToTopText(lang) {
    const texts = {
      en: 'Back to Top',
      sc: '返回顶部',
      tc: '返回頂部',
      th: 'กลับสู่ด้านบน'
    };
    return texts[lang] || texts.en;
  }

  /**
   * Change language and update configuration
   * @param {string} language - New language code
   * @returns {Promise} - Language change promise
   */
  async changeLanguage(language) {
    if (this.i18nInstance && this.config.i18n.supportedLanguages.includes(language)) {
      await this.i18nInstance.changeLanguage(language);
      
      // Update HTML lang attribute
      document.documentElement.lang = language;
      
      // Update direction for RTL languages
      if (this.config.i18n.rtlLanguages.includes(language)) {
        document.documentElement.dir = 'rtl';
      } else {
        document.documentElement.dir = 'ltr';
      }
      
      return true;
    }
    return false;
  }

  /**
   * Get current language
   * @returns {string} - Current language code
   */
  getCurrentLanguage() {
    return this.i18nInstance?.language || this.config.i18n.defaultLanguage;
  }

  /**
   * Get supported languages with names
   * @returns {Array} - Array of language objects
   */
  getSupportedLanguages() {
    return this.config.i18n.supportedLanguages.map(lang => ({
      code: lang,
      name: this.config.i18n.languageNames[lang] || lang,
      isRTL: this.config.i18n.rtlLanguages.includes(lang)
    }));
  }

  /**
   * Format date according to language settings
   * @param {Date} date - Date to format
   * @param {string} lang - Language code
   * @returns {string} - Formatted date
   */
  formatDate(date, lang = null) {
    const language = lang || this.getCurrentLanguage();
    const locale = this.config.i18n.numberFormat[language] || 'en-US';
    
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  }

  /**
   * Format number according to language settings
   * @param {number} number - Number to format
   * @param {string} lang - Language code
   * @returns {string} - Formatted number
   */
  formatNumber(number, lang = null) {
    const language = lang || this.getCurrentLanguage();
    const locale = this.config.i18n.numberFormat[language] || 'en-US';
    
    return new Intl.NumberFormat(locale).format(number);
  }

  /**
   * Get i18n instance
   * @returns {Object} - i18n instance
   */
  getInstance() {
    return this.i18nInstance;
  }
}

export default I18nManager;